import { useState, useEffect, useCallback } from 'react'
import { useAuth } from '../../contexts/AuthContext'
import './Friends.css'

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8080/api/v1'

export default function Friends({ onBack }) {
  const { user, session, isGuest, triggerRegistration } = useAuth()
  const [activeTab, setActiveTab] = useState('friends')
  const [friends, setFriends] = useState([])
  const [friendRequests, setFriendRequests] = useState([])
  const [searchResults, setSearchResults] = useState([])
  const [searchQuery, setSearchQuery] = useState('')
  const [loading, setLoading] = useState(true)
  const [searchLoading, setSearchLoading] = useState(false)
  const [error, setError] = useState(null)

  const loadFriends = async (token) => {
    const response = await fetch(`${API_BASE}/friends`, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    })

    if (!response.ok) {
      throw new Error('Failed to fetch friends')
    }

    const data = await response.json()
    setFriends(data.friends || [])
  }

  const loadFriendRequests = async (token) => {
    const response = await fetch(`${API_BASE}/friends/requests?type=received`, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    })

    if (!response.ok) {
      throw new Error('Failed to fetch friend requests')
    }

    const data = await response.json()
    setFriendRequests(data.requests || [])
  }

  const loadData = useCallback(async () => {
    if (!user || !session?.access_token) return

    setLoading(true)
    setError(null)

    try {
      const token = session.access_token

      if (activeTab === 'friends') {
        await loadFriends(token)
      } else if (activeTab === 'requests') {
        await loadFriendRequests(token)
      }
    } catch (err) {
      console.error('Error loading data:', err)
      setError('Failed to load data')
    } finally {
      setLoading(false)
    }
  }, [user, session, activeTab])

  useEffect(() => {
    // Push a new state to the history when the component mounts
    window.history.pushState({ page: 'friends' }, '', '#friends')

    // Handle browser back button
    const handlePopState = () => {
      onBack()
    }

    // Handle friend request updates from notifications
    const handleFriendRequestUpdate = () => {
      console.log('FRIENDS DEBUG: Received friend-request-updated event, refreshing data')
      loadData()
    }

    window.addEventListener('popstate', handlePopState)
    window.addEventListener('friend-request-updated', handleFriendRequestUpdate)

    // Cleanup on unmount
    return () => {
      window.removeEventListener('popstate', handlePopState)
      window.removeEventListener('friend-request-updated', handleFriendRequestUpdate)
    }
  }, [onBack, loadData])

  useEffect(() => {
    loadData()
  }, [activeTab, loadData])

  const searchUsers = async (query) => {
    if (!user || !session?.access_token || query.length < 2) {
      setSearchResults([])
      return
    }

    setSearchLoading(true)
    try {
      const token = session.access_token
      const response = await fetch(`${API_BASE}/friends/search?q=${encodeURIComponent(query)}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      })

      if (!response.ok) {
        throw new Error('Search failed')
      }

      const data = await response.json()
      setSearchResults(data.users || [])
    } catch (err) {
      console.error('Search error:', err)
      setSearchResults([])
    } finally {
      setSearchLoading(false)
    }
  }

  const sendFriendRequest = async (receiverId) => {
    if (!user || !session?.access_token) return

    try {
      const token = session.access_token
      const response = await fetch(`${API_BASE}/friends/request`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          receiver_id: receiverId,
          message: '',
        }),
      })

      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.error || 'Failed to send friend request')
      }

      // Remove from search results
      setSearchResults(prev => prev.filter(user => user.id !== receiverId))
      alert('Friend request sent!')
    } catch (err) {
      console.error('Error sending friend request:', err)
      alert(err.message)
    }
  }

  const acceptFriendRequest = async (requestId) => {
    if (!user || !session?.access_token) return

    try {
      const token = session.access_token
      const response = await fetch(`${API_BASE}/friends/requests/${requestId}/accept`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      })

      if (!response.ok) {
        throw new Error('Failed to accept friend request')
      }

      // Reload data
      await loadFriendRequests(session.access_token)
      if (activeTab === 'friends') {
        await loadFriends(session.access_token)
      }
    } catch (err) {
      console.error('Error accepting friend request:', err)
      alert('Failed to accept friend request')
    }
  }

  const declineFriendRequest = async (requestId) => {
    if (!user || !session?.access_token) return

    try {
      const token = session.access_token
      const response = await fetch(`${API_BASE}/friends/requests/${requestId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      })

      if (!response.ok) {
        throw new Error('Failed to decline friend request')
      }

      // Reload data
      await loadFriendRequests(session.access_token)
    } catch (err) {
      console.error('Error declining friend request:', err)
      alert('Failed to decline friend request')
    }
  }

  const removeFriend = async (friendId) => {
    if (!user || !session?.access_token) return

    if (!window.confirm('Are you sure you want to remove this friend?')) {
      return
    }

    try {
      const token = session.access_token
      const response = await fetch(`${API_BASE}/friends/${friendId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      })

      if (!response.ok) {
        throw new Error('Failed to remove friend')
      }

      // Reload friends list
      await loadFriends(session.access_token)
    } catch (err) {
      console.error('Error removing friend:', err)
      alert('Failed to remove friend')
    }
  }

  const challengeFriend = async (friendId, friendUsername) => {
    if (!user || !session?.access_token) {
      if (window.showToast) {
        window.showToast('Please log in to challenge friends', 'error')
      }
      return
    }

    console.log('Challenging friend - User:', user)
    console.log('User ID:', user?.id)
    console.log('Friend ID:', friendId, 'Friend Username:', friendUsername)

    // Prevent challenging yourself
    if (user?.id === friendId) {
      if (window.showToast) {
        window.showToast('You cannot challenge yourself!', 'error')
      }
      return
    }

    try {
      const token = session.access_token
      const response = await fetch(`${API_BASE}/games`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          time_control: {
            initial_time: 600, // 10 minutes in seconds
            increment: 0       // no increment
          },
          rated: false,
          private: true,
          invited_player_id: friendId
        }),
      })

      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.error || 'Failed to create game challenge')
      }

      const data = await response.json()

      // Show success message with toaster
      if (window.showToast) {
        window.showToast(`⚔️ Challenge sent to ${friendUsername}!`, 'success', 4000)
      }
    } catch (err) {
      console.error('Error challenging friend:', err)
      console.error('User object:', user)

      // Show error message with toaster
      if (window.showToast) {
        window.showToast(`Failed to challenge ${friendUsername}: ${err.message}`, 'error', 5000)
      }
    }
  }

  const handleSearchChange = (e) => {
    const query = e.target.value
    setSearchQuery(query)
    searchUsers(query)
  }

  const formatLastSeen = (lastSeen) => {
    if (!lastSeen) return 'Never'

    const now = new Date()
    const lastSeenDate = new Date(lastSeen)
    const diffMs = now - lastSeenDate
    const diffMins = Math.floor(diffMs / (1000 * 60))
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60))
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

    if (diffMins < 5) return 'Online'
    if (diffMins < 60) return `${diffMins}m ago`
    if (diffHours < 24) return `${diffHours}h ago`
    if (diffDays === 1) return 'Yesterday'
    if (diffDays < 7) return `${diffDays}d ago`

    return lastSeenDate.toLocaleDateString()
  }

  const renderFriendsList = () => (
    <div className="friends-list">
      {friends.length === 0 ? (
        <div className="empty-state">
          <p>You don't have any friends yet.</p>
          <p>Search for players to add them as friends!</p>
        </div>
      ) : (
        friends.map(friend => (
          <div key={friend.id} className="friend-item">
            <div className="friend-info">
              <div className="friend-name">
                <span className={`status-indicator ${friend.is_online ? 'online' : 'offline'}`}></span>
                {friend.username}
              </div>
              <div className="friend-details">
                <span className="rating">{friend.rating}</span>
                <span className="games">{friend.games_played} games</span>
                <span className="last-seen">{formatLastSeen(friend.last_seen)}</span>
              </div>
            </div>
            <div className="friend-actions">
              <button
                className="challenge-btn"
                onClick={() => challengeFriend(friend.id, friend.username)}
              >
                Challenge
              </button>
              <button
                className="remove-btn"
                onClick={() => removeFriend(friend.id)}
              >
                Remove
              </button>
            </div>
          </div>
        ))
      )}
    </div>
  )

  const renderFriendRequests = () => (
    <div className="friend-requests">
      {friendRequests.length === 0 ? (
        <div className="empty-state">
          <p>No pending friend requests.</p>
        </div>
      ) : (
        friendRequests.map(request => (
          <div key={request.id} className="request-item">
            <div className="request-info">
              <div className="sender-name">{request.sender?.username}</div>
              <div className="sender-details">
                <span className="rating">{request.sender?.rating}</span>
                <span className="games">{request.sender?.games_played} games</span>
              </div>
              {request.message && (
                <div className="request-message">{request.message}</div>
              )}
            </div>
            <div className="request-actions">
              <button
                className="accept-btn"
                onClick={() => acceptFriendRequest(request.id)}
              >
                Accept
              </button>
              <button
                className="decline-btn"
                onClick={() => declineFriendRequest(request.id)}
              >
                Decline
              </button>
            </div>
          </div>
        ))
      )}
    </div>
  )

  const renderSearch = () => (
    <div className="user-search">
      <div className="search-box">
        <input
          type="text"
          placeholder="Search for players..."
          value={searchQuery}
          onChange={handleSearchChange}
          className="search-input"
        />
      </div>

      {searchLoading && (
        <div className="search-loading">Searching...</div>
      )}

      {searchResults.length > 0 && (
        <div className="search-results">
          {searchResults.map(user => (
            <div key={user.id} className="search-result-item">
              <div className="user-info">
                <div className="user-name">{user.username}</div>
                <div className="user-details">
                  <span className="rating">{user.rating}</span>
                  <span className="games">{user.games_played} games</span>
                </div>
              </div>
              <button
                className="add-friend-btn"
                onClick={() => sendFriendRequest(user.id)}
              >
                Add Friend
              </button>
            </div>
          ))}
        </div>
      )}

      {searchQuery.length >= 2 && !searchLoading && searchResults.length === 0 && (
        <div className="no-results">No players found.</div>
      )}
    </div>
  )

  if (isGuest) {
    return (
      <div className="friends-container">
        <div className="friends-content">
          <div className="empty-state">
            <h3>Create an Account</h3>
            <p>Sign up to add friends and connect with other players.</p>
            <button onClick={triggerRegistration} className="create-account-button">
              Create Account
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="friends-container">
      <div className="friends-header">
        <h1>Friends</h1>
        <div className="friends-tabs">
          <button
            className={`tab ${activeTab === 'friends' ? 'active' : ''}`}
            onClick={() => setActiveTab('friends')}
          >
            Friends ({friends.length})
          </button>
          <button
            className={`tab ${activeTab === 'requests' ? 'active' : ''}`}
            onClick={() => setActiveTab('requests')}
          >
            Requests ({friendRequests.length})
          </button>
          <button
            className={`tab ${activeTab === 'search' ? 'active' : ''}`}
            onClick={() => setActiveTab('search')}
          >
            Add Friends
          </button>
        </div>
      </div>

      <div className="friends-content">
        {error && (
          <div className="error-message">{error}</div>
        )}

        {loading && (
          <div className="loading">Loading...</div>
        )}

        {!loading && !error && (
          <>
            {activeTab === 'friends' && renderFriendsList()}
            {activeTab === 'requests' && renderFriendRequests()}
            {activeTab === 'search' && renderSearch()}
          </>
        )}
      </div>
    </div>
  )
}